import { create } from 'zustand';

import { type HistorySnapshot, useNodeStore } from '@/stores/node-store';
import { workflowContentKey } from '@/utils/workflow-content-key';

export type ChatApplyStatus = 'applied' | 'undone';

interface ChatApplyRecord {
  status: ChatApplyStatus;
  // Dropped when the undo history is cleared (every workflow load): the
  // status outlives them so the card still reads right, but it can no
  // longer undo.
  snapshots: { before: HistorySnapshot; after: HistorySnapshot } | null;
}

export interface ChatApplySaveReport {
  summary: string;
  tone: 'success' | 'warning';
}

export type ChatApplySaveHandler = (
  report: ChatApplySaveReport,
) => Promise<void>;

interface RegisteredSaveHandler {
  save: ChatApplySaveHandler;
  // The workflow the registering editor shows. A queued save only runs
  // against the workflow it was requested for: the handler reads the canvas
  // from the global node store, so running it after a switch would write
  // the wrong graph (or skip the right one).
  workflowKey: string;
}

interface ChatApplyState {
  // Keyed by the chat message whose card was applied. The snapshots are the
  // node-store history entries either side of the apply: undo/redo is
  // detected by snapshot identity, which survives index shifts and goes
  // stale on its own once a later edit truncates the redo branch.
  records: Record<string, ChatApplyRecord>;
  saveHandler: RegisteredSaveHandler | null;
  setSaveHandler: (save: ChatApplySaveHandler, workflowKey: string) => void;
  // Only clears when `handler` is still the registered one, so an editor
  // unmounting after another mounted doesn't drop the newer handler.
  clearSaveHandler: (handler: ChatApplySaveHandler) => void;
  recordApply: (
    key: string,
    before: HistorySnapshot,
    after: HistorySnapshot,
  ) => void;
  forgetApply: (key: string) => void;
}

export const useChatApplyStore = create<ChatApplyState>((set) => ({
  records: {},
  saveHandler: null,
  setSaveHandler: (save, workflowKey) =>
    set({ saveHandler: { save, workflowKey } }),
  clearSaveHandler: (save) =>
    set((state) =>
      state.saveHandler?.save === save ? { saveHandler: null } : state,
    ),
  recordApply: (key, before, after) => {
    ensureHistoryListener();
    set((state) => ({
      records: {
        ...state.records,
        [key]: { status: 'applied', snapshots: { before, after } },
      },
    }));
  },
  forgetApply: (key) =>
    set((state) => {
      if (!(key in state.records)) return state;
      const records = { ...state.records };
      delete records[key];
      return { records };
    }),
}));

let saveInFlight: Promise<void> | null = null;
let queued: { report: ChatApplySaveReport; workflowKey: string } | null = null;

/**
 * Persist the canvas after a chat apply (or its undo/redo) through the
 * mounted editor's save handler. Saves are serialized and coalesced: a
 * request made while one is in flight runs once after it, against the
 * canvas as it is then, so a quick apply-then-undo can't land out of order.
 */
export function requestChatApplySave(report: ChatApplySaveReport): void {
  const handler = useChatApplyStore.getState().saveHandler;
  if (!handler) return;
  if (saveInFlight) {
    queued = { report, workflowKey: handler.workflowKey };
    return;
  }
  saveInFlight = handler.save(report).finally(() => {
    saveInFlight = null;
    const next = queued;
    queued = null;
    if (
      next &&
      useChatApplyStore.getState().saveHandler?.workflowKey === next.workflowKey
    ) {
      requestChatApplySave(next.report);
    }
  });
}

export type UndoChatApplyResult = 'undone' | 'edited-since' | 'unavailable';

/**
 * Undo the canvas changes a chat card applied. Only allowed while that apply
 * is still the latest thing on the canvas -- undoing past later edits would
 * throw them away. The status flip and save happen in the history listener
 * below, the same path a canvas undo takes.
 */
export function undoChatApply(key: string): UndoChatApplyResult {
  const record = useChatApplyStore.getState().records[key];
  if (!record?.snapshots || record.status !== 'applied') return 'unavailable';
  const { before, after } = record.snapshots;

  const { history, currentHistoryIndex, nodes, edges, undo } =
    useNodeStore.getState();
  if (history[currentHistoryIndex - 1] !== before) {
    return history.includes(after) ? 'edited-since' : 'unavailable';
  }
  // Text edits don't push history snapshots, so the index alone can't prove
  // the canvas is untouched since the apply.
  if (
    history[currentHistoryIndex] !== after ||
    workflowContentKey({ nodes, edges }) !== workflowContentKey(after)
  ) {
    return 'edited-since';
  }
  undo();
  return 'undone';
}

let unsubscribeHistoryListener: (() => void) | null = null;

// Started on the first recorded apply rather than at import, so merely
// importing this module doesn't reach into the node store.
function ensureHistoryListener() {
  if (unsubscribeHistoryListener) return;
  unsubscribeHistoryListener = useNodeStore.subscribe(reconcileWithHistory);
}

function reconcileWithHistory(
  state: ReturnType<typeof useNodeStore.getState>,
  prev: ReturnType<typeof useNodeStore.getState>,
) {
  if (
    state.history === prev.history &&
    state.currentHistoryIndex === prev.currentHistoryIndex
  ) {
    return;
  }
  const { records } = useChatApplyStore.getState();
  const keys = Object.keys(records);
  if (keys.length === 0) return;

  if (state.history.length === 0) {
    const cleared = Object.fromEntries(
      keys.map((key) => [key, { ...records[key], snapshots: null }]),
    );
    useChatApplyStore.setState({ records: cleared });
    return;
  }

  const current = state.history[state.currentHistoryIndex];
  const next = state.history[state.currentHistoryIndex + 1];
  let flippedTo: ChatApplyStatus | null = null;
  const updated = { ...records };
  for (const key of keys) {
    const record = records[key];
    if (!record.snapshots) continue;
    const { before, after } = record.snapshots;
    if (record.status === 'applied' && current === before && next === after) {
      updated[key] = { ...record, status: 'undone' };
      flippedTo = 'undone';
    } else if (record.status === 'undone' && current === after) {
      updated[key] = { ...record, status: 'applied' };
      flippedTo = 'applied';
    }
  }
  if (!flippedTo) return;

  useChatApplyStore.setState({ records: updated });
  requestChatApplySave({
    summary:
      flippedTo === 'undone'
        ? 'Undid the chat changes'
        : 'Reapplied the chat changes',
    tone: 'success',
  });
}
